import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Clock, SquareIcon, Calendar, Users, Building2 } from "lucide-react";

interface CleaningArea {
  id: string;
  name: string;
  squareFootage: number;
  frequency: string;
  productionRate: number;
  hoursPerDay: number;
  daysPerWeek: number;
  specialRequirements?: string;
  calculatedHours: number;
  type?: string;
  laborRate?: number;
  cost?: number;
  overheadPercentage?: number;
  profitMargin?: number;
}

interface ViewCleaningAreaSheetProps {
  isOpen: boolean;
  onClose: () => void;
  area: CleaningArea;
  onEdit?: (area: CleaningArea) => void;
}

export function ViewCleaningAreaSheet({
  isOpen,
  onClose,
  area,
  onEdit,
}: ViewCleaningAreaSheetProps) {
  // Format frequency for display
  const formatFrequency = (frequency: string) => {
    if (frequency.includes("_per_")) {
      return frequency.replace("_per_", " per ").replace("_", " ");
    }

    // Handle standard frequency formats
    switch (frequency) {
      case "daily":
        return "Daily (Mon-Fri)";
      case "weekly":
        return "Weekly";
      case "biweekly":
        return "Bi-Weekly";
      case "monthly":
        return "Monthly";
      case "quarterly":
        return "Quarterly";
      case "semiannually":
        return "Semi-Annually";
      case "annually":
        return "Annually";
      default:
        return frequency.charAt(0).toUpperCase() + frequency.slice(1);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(area);
    } else {
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Cleaning Area Details</SheetTitle>
          <SheetDescription>View details for {area.name} cleaning area</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            {/* Area Information */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{area.name}</h3>
                <Badge variant="outline" className="bg-blue-50 text-blue-600">
                  Cleaning Area
                </Badge>
              </div>

              {area.specialRequirements && (
                <div className="text-sm text-gray-600">
                  <p>{area.specialRequirements}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* Area Details */}
            <div className="space-y-4">
              <h4 className="font-medium">Area Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center rounded-md bg-gray-50 p-3">
                  <SquareIcon className="mr-2 h-4 w-4 text-blue-500" />
                  <div>
                    <div className="text-sm text-gray-500">Square Footage</div>
                    <div className="text-lg font-semibold">
                      {area.squareFootage.toLocaleString()} sq ft
                    </div>
                  </div>
                </div>
                <div className="flex items-center rounded-md bg-gray-50 p-3">
                  <Clock className="mr-2 h-4 w-4 text-blue-500" />
                  <div>
                    <div className="text-sm text-gray-500">Production Rate</div>
                    <div className="text-lg font-semibold">
                      {area.productionRate.toLocaleString()} sq ft/hr
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Schedule Information */}
            <div className="space-y-4">
              <h4 className="font-medium">Schedule Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center rounded-md bg-gray-50 p-3">
                  <Calendar className="mr-2 h-4 w-4 text-blue-500" />
                  <div>
                    <div className="text-sm text-gray-500">Frequency</div>
                    <div className="text-lg font-semibold">{formatFrequency(area.frequency)}</div>
                  </div>
                </div>
                <div className="flex items-center rounded-md bg-gray-50 p-3">
                  <Calendar className="mr-2 h-4 w-4 text-blue-500" />
                  <div>
                    <div className="text-sm text-gray-500">Days Per Week</div>
                    <div className="text-lg font-semibold">{area.daysPerWeek}</div>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Hours Calculation */}
            <div className="space-y-4">
              <h4 className="font-medium">Hours Calculation</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center rounded-md bg-gray-50 p-3">
                  <Clock className="mr-2 h-4 w-4 text-blue-500" />
                  <div>
                    <div className="text-sm text-gray-500">Hours Per Day</div>
                    <div className="text-lg font-semibold">{area.hoursPerDay} hrs</div>
                  </div>
                </div>
                <div className="flex items-center rounded-md bg-blue-50 p-3">
                  <Clock className="mr-2 h-4 w-4 text-blue-500" />
                  <div>
                    <div className="text-sm text-gray-500">Weekly Hours</div>
                    <div className="text-lg font-semibold text-blue-600">
                      {area.calculatedHours} hrs
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-md bg-blue-50 p-3">
                <div className="mb-2 text-sm font-medium">Hours Calculation Method</div>
                <div className="text-sm">
                  {area.squareFootage > 0 && area.productionRate > 0 ? (
                    <>
                      <p>
                        {area.squareFootage.toLocaleString()} sq ft ÷{" "}
                        {area.productionRate.toLocaleString()} sq ft/hr = {area.hoursPerDay} hrs/day
                      </p>
                      <p className="mt-1">
                        {area.hoursPerDay} hrs/day × {area.daysPerWeek} days/week ={" "}
                        {area.calculatedHours} hrs/week
                      </p>
                    </>
                  ) : (
                    <p>Hours entered manually: {area.calculatedHours} hrs/week</p>
                  )}
                </div>
              </div>
            </div>

            {/* Cost Information - if available */}
            {area.laborRate && area.cost && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h4 className="font-medium">Cost Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-md bg-gray-50 p-3">
                      <div className="text-sm text-gray-500">Labor Rate</div>
                      <div className="text-lg font-semibold">
                        {formatCurrency(area.laborRate)}/hr
                      </div>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <div className="text-sm text-gray-500">Weekly Cost</div>
                      <div className="text-lg font-semibold">{formatCurrency(area.cost)}</div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="w-full" onClick={onClose}>
                Close
              </Button>
              {onEdit && (
                <Button className="w-full" onClick={handleEdit}>
                  Edit Area
                </Button>
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
